import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  Chip,
  CircularProgress,
  Tooltip,
  Modal,
  Paper,
  Button,
  Grid,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  AccountTree as AccountTreeIcon,
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  Folder as FolderIcon,
  Close as CloseIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Tune as TuneIcon,
  Store as StoreIcon,
  CreditCard as CreditCardIcon,
  AutoAwesome as AutoAwesomeIcon,
  Image as ImageIcon,
  Category as CategoryIcon,
  Palette as PaletteIcon,
  ViewInAr as ViewInArIcon,
  Storage as StorageIcon,
  Router as RouterIcon,
  Api as ApiIcon,
  Description as DescriptionIcon,
  SystemUpdate as SystemUpdateIcon,
  CardGiftcard as CardGiftcardIcon,
  Lock as LockIcon,
  Domain as DomainIcon,
  Extension as ExtensionIcon,
  AssignmentInd as AssignmentIndIcon,
  Subscriptions as SubscriptionsIcon,
  FormatListBulleted as FormatListBulletedIcon,
  BrandingWatermark as BrandingWatermarkIcon,
  Feedback as FeedbackIcon,
  ContactMail as ContactMailIcon,
  Inventory2 as Inventory2Icon,
  CardMembership as CardMembershipIcon,
  ReceiptLong as ReceiptLongIcon,
  LocalOffer as LocalOfferIcon,
  PriceChange as PriceChangeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import PermissionRefreshNotification from '../Common/PermissionRefreshNotification';

const drawerWidth = 240;
// Narrow rail when the admin sidebar is collapsed (desktop only; mobile still uses full temporary drawer).
const drawerWidthCollapsed = 72;

const getModuleIcon = (iconName) => {
  const icons = {
    // Generic icon names
    'dashboard': <DashboardIcon />,
    'people': <PeopleIcon />,
    'settings': <SettingsIcon />,
    'security': <SecurityIcon />,
    'business': <BusinessIcon />,
    'account_tree': <AccountTreeIcon />,
    'assignment': <AssignmentIcon />,
    'history': <HistoryIcon />,
    'person': <PersonIcon />,
    'folder': <FolderIcon />,
    'store': <StoreIcon />,
    'tune': <TuneIcon />,
    'credit_card': <CreditCardIcon />,
    'wallet': <AccountBalanceWalletIcon />,
    'auto_awesome': <AutoAwesomeIcon />,
    'image': <ImageIcon />,
    'category': <CategoryIcon />,
    'palette': <PaletteIcon />,
    'view_in_ar': <ViewInArIcon />,

    // Master Data & Configuration
    'storage': <StorageIcon />,
    'router': <RouterIcon />,

    // API & Integration
    'api': <ApiIcon />,
    'description': <DescriptionIcon />,

    // System Management
    'system_update': <SystemUpdateIcon />,
    'lock': <LockIcon />,
    'domain': <DomainIcon />,
    'extension': <ExtensionIcon />,

    // Roles & Permissions
    'assignment_ind': <AssignmentIndIcon />,

    // Subscription & Payments
    'subscriptions': <SubscriptionsIcon />,
    'card_giftcard': <CardGiftcardIcon />,
    'account_balance_wallet': <AccountBalanceWalletIcon />,

    // Subscription v2 modules
    'inventory_2': <Inventory2Icon />,
    'card_membership': <CardMembershipIcon />,
    'receipt_long': <ReceiptLongIcon />,
    'local_offer': <LocalOfferIcon />,
    'price_change': <PriceChangeIcon />,

    // Content Management
    'format_list_bulleted': <FormatListBulletedIcon />,
    'branding_watermark': <BrandingWatermarkIcon />,
    'feedback': <FeedbackIcon />,
    'contact_mail': <ContactMailIcon />,
  };
  return icons[iconName] || <FolderIcon />;
};

const formatModuleName = (moduleName) => {
  // Display labels only — module keys (e.g. CreditSystemManagement) must match DB/permission slugs.
  const specialCases = {
    'UserRoles': 'User Roles',
    'MerchantManagement': 'Merchant Management',
    'CreditSystemManagement': 'Token System Management',
    'MasterDataConfiguration': 'Master Data Configuration',
    'ContactEnquiries': 'Contact Enquiries'
  };

  // If it's a special case, return the custom format
  if (specialCases[moduleName]) {
    return specialCases[moduleName];
  }

  // For other camelCase names, add spaces before capital letters
  return moduleName.replace(/([A-Z])/g, ' $1').trim();
};

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const {
    menu,
    hasAnyPermission,
    hasPermission,
    permissions,
    loading: permissionsLoading,
    lastRefresh
  } = usePermissions();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  // Always start expanded on load/refresh; collapse is session-only (not persisted).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const effectiveDrawerWidth = sidebarCollapsed ? drawerWidthCollapsed : drawerWidth;

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/glam-admin/login');
    handleProfileMenuClose();
  };

  const handleMenuClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const toggleMenuExpand = (module) => {
    setExpandedMenus(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  const handleProfileModalOpen = () => {
    setProfileModalOpen(true);
    handleProfileMenuClose();
  };

  const handleProfileModalClose = () => {
    setProfileModalOpen(false);
  };

  // mini=true: icon rail + tooltips (desktop collapsed). mini=false: full labels (desktop expanded + mobile overlay).
  // showCollapseToggle: only on the permanent desktop drawer — collapse control lives on the sidebar, not the app bar.
  const renderDrawerContent = (mini, showCollapseToggle = false) => (
    <div>
      {showCollapseToggle ? (
        <Toolbar
          sx={{
            minHeight: 56,
            px: mini ? 0.5 : 1,
            justifyContent: mini ? 'center' : 'space-between',
            alignItems: 'center',
            gap: mini ? 0 : 0.5,
            flexDirection: 'row',
            py: mini ? 0.5 : 0,
          }}
        >
          {mini ? (
            <Tooltip title="Expand menu" placement="right">
              <IconButton
                size="small"
                onClick={toggleSidebarCollapsed}
                aria-label="Expand sidebar"
                sx={{ color: 'text.secondary' }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Typography variant="h6" noWrap component="div" sx={{ flex: 1, minWidth: 0, fontSize: '1.1rem' }}>
                Glammify Admin
              </Typography>
              <Tooltip title="Collapse menu" placement="bottom">
                <IconButton
                  size="small"
                  onClick={toggleSidebarCollapsed}
                  aria-label="Collapse sidebar"
                  edge="end"
                  sx={{ flexShrink: 0, color: 'text.secondary' }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Toolbar>
      ) : (
        <Toolbar sx={{ px: 1, minHeight: 56 }}>
          <Typography variant="h6" noWrap component="div" sx={{ fontSize: '1.1rem' }}>
            Glammify Admin
          </Typography>
        </Toolbar>
      )}
      <Divider />

      <List>
        {menu.map((moduleItem, index) => {
          if (hasPermission(moduleItem.module, 'view')) {
            const path = `/glam-admin/${moduleItem.route}`;
            const isActive = location.pathname === path;
            const label = formatModuleName(moduleItem.module);

            const listBtn = (
              <ListItemButton
                selected={isActive}
                onClick={() => handleMenuClick(path)}
                sx={{
                  justifyContent: mini ? 'center' : 'flex-start',
                  px: mini ? 1 : 2,
                  minHeight: 48,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: mini ? 0 : 48,
                    justifyContent: 'center',
                    mx: mini ? 0 : undefined,
                  }}
                >
                  {getModuleIcon(moduleItem.icon)}
                </ListItemIcon>
                {!mini ? <ListItemText primary={label} /> : null}
              </ListItemButton>
            );

            return (
              <ListItem key={`module-${index}`} disablePadding>
                {mini ? (
                  <Tooltip title={label} placement="right" arrow>
                    {listBtn}
                  </Tooltip>
                ) : (
                  listBtn
                )}
              </ListItem>
            );
          }
          return null;
        })}
      </List>
    </div>
  );

  // Profile Modal Component
  const ProfileModal = () => (
    <Modal
      open={profileModalOpen}
      onClose={handleProfileModalClose}
      aria-labelledby="profile-modal-title"
      aria-describedby="profile-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: 700 },
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 24,
        p: 0,
        maxHeight: '85vh',
        overflow: 'hidden',
      }}>
        <Paper elevation={0}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <Typography variant="h5" component="h2" id="profile-modal-title">
              User Profile
            </Typography>
            <IconButton onClick={handleProfileModalClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Profile Content */}
          <Box sx={{ p: 3, overflow: 'auto', maxHeight: 'calc(85vh - 140px)' }}>
            <Grid container spacing={3}>
              {/* Profile Picture and Name */}
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar sx={{
                    width: 100,
                    height: 100,
                    fontSize: '2.5rem',
                    mb: 2,
                    bgcolor: 'primary.main'
                  }}>
                    {user && user.name && user.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="h6" textAlign="center" sx={{ wordBreak: 'break-word', mb: 1 }}>
                    {user && user.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{
                      wordBreak: 'break-word',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.2
                    }}
                  >
                    {user && user.email}
                  </Typography>
                </Box>
              </Grid>

              {/* Profile Details */}
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      User ID
                    </Typography>
                    <Typography variant="body1">
                      {user && user.id}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email Address
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {user && user.email}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Company
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {user && user.company_name}
                    </Typography>
                  </Box>

                  {user && user.department && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Department
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          wordBreak: 'break-word',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {user.department}
                      </Typography>
                    </Box>
                  )}

                  {user && user.role && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Role
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          wordBreak: 'break-word',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {user.role}
                      </Typography>
                    </Box>
                  )}

                  {user && user.created_at && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Account Created
                      </Typography>
                      <Typography variant="body1">
                        {new Date(user.created_at).toLocaleDateString('en-GB')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 3,
            borderTop: 1,
            borderColor: 'divider'
          }}>
            <Button variant="contained" onClick={handleProfileModalClose}>
              Close
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={(theme) => ({
          width: { xs: '100%', sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { sm: `${effectiveDrawerWidth}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="Open navigation menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {location.pathname.replace('/', '').replace('-', ' ').toUpperCase() || 'DASHBOARD'}
          </Typography>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            minWidth: 0,
            flexShrink: 0
          }}>
            {permissionsLoading && (
              <Tooltip title="Refreshing permissions...">
                <CircularProgress size={20} color="inherit" />
              </Tooltip>
            )}

            <Typography
              variant="body2"
              sx={{
                display: { xs: 'none', sm: 'block' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: { sm: '150px', md: '200px' }
              }}
            >
              {user && user.company_name}
            </Typography>

            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{
                flexShrink: 0,
                minWidth: 'auto'
              }}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user && user.name && user.name.charAt(0)}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem onClick={handleProfileModalOpen}>
          <PersonIcon sx={{ mr: 1 }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { sm: effectiveDrawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="Admin navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {renderDrawerContent(false, false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={(theme) => ({
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: effectiveDrawerWidth,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          })}
          open
        >
          {renderDrawerContent(sidebarCollapsed, true)}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={(theme) => ({
          flexGrow: 1,
          p: { xs: 0.5, sm: 1 },
          width: { xs: '100%', sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          overflow: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        })}
      >
        <Toolbar />
        <Box sx={{
          height: '100%',
          overflow: 'auto',
          '& > *': {
            maxWidth: '100%'
          }
        }}>
          {children}
        </Box>

        {/* Permission refresh notification */}
        <PermissionRefreshNotification />
      </Box>

      {/* Profile Modal */}
      <ProfileModal />
    </Box>
  );
};

export default Layout; 